import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { currencySymbol, paymentMethodOptions } from 'utils/constant';

import CustomTypography from 'components/Fileds/CustomTypography';
import CustomButton from 'components/Fileds/CustomButton';
import CustomSelect from 'components/Fileds/CustomSelect';

const SplitBillsView = ({ orderDetails, onPaySplitBill, onPrintSplitBill, isPaying, onRemoveSplitBill, isRemoving }) => {
  const vendorData = useSelector((state) => state?.posData?.vendor);
  const symbol = currencySymbol(vendorData?.currency);

  const [selectedSplitToPay, setSelectedSplitToPay] = useState(null);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [payModalOpen, setPayModalOpen] = useState(false);

  const splits = orderDetails?.orderSplits || [];

  if (splits.length === 0) return null;

  const handleOpenPayModal = (split) => {
    setSelectedSplitToPay(split);
    setPaymentMode('cash');
    setPayModalOpen(true);
  };

  const handleConfirmPay = () => {
    if (!selectedSplitToPay) return;

    const isItemWise = selectedSplitToPay.split_type === 'item_wise';
    let calculatedTotal = parseFloat(selectedSplitToPay.total_amount || 0).toFixed(2);

    if (isItemWise) {
      const sTotal = parseFloat(selectedSplitToPay?.sub_total || 0);
      const sTax = parseFloat(selectedSplitToPay?.tax_amount || 0);

      const sTaxPct = parseFloat(vendorData?.service_tax || 0);
      const pFeePct = 0; // In POS orders, customer does not pay platform fee
      let serviceTaxVal = 0;
      let platformFeeVal = 0;

      if (sTax > 0) {
        serviceTaxVal = sTax;
      } else {
        serviceTaxVal = (sTotal * sTaxPct) / 100;
      }

      calculatedTotal = (sTotal + serviceTaxVal).toFixed(2);
    }

    onPaySplitBill({
      order_split_id: selectedSplitToPay.id,
      payment_mode: paymentMode,
      total_amount: calculatedTotal,
    });
    setPayModalOpen(false);
  };

  const hasAnyPaidSplit = splits.some((s) => s.payment_status === 'paid');

  const isOrderFinalized = ['completed', 'rejected', 'cancelled'].includes(
    (orderDetails?.order_status || orderDetails?.status || '').toLowerCase()
  );

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#000', fontWeight: 700 }}>
          Separate Split Bills ({splits.length} Bills)
        </Typography>

        {!hasAnyPaidSplit && !isOrderFinalized && onRemoveSplitBill && (
          <CustomButton
            variant="outlined"
            onClick={onRemoveSplitBill}
            disabled={isRemoving}
            sx={{
              // borderColor: '#',
              color: '#fff',
              bgcolor: '#000',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '8px',
              height: '38px',
            }}
          >
            Cancel Split Bill
          </CustomButton>
        )}
      </Box>

      {/* Grid of Split Bill Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>
        {splits.map((split) => {
          const isPaid = split.payment_status === 'paid';
          const isItemWise = split.split_type === 'item_wise';

          const sTaxPct = parseFloat(vendorData?.service_tax || 0);
          const pFeePct = 0; // In POS orders, customer does not pay platform fee
          let subTotal = parseFloat(split.sub_total || 0);
          let tax = parseFloat(split.tax_amount || 0);
          let serviceTaxVal = 0;
          let platformFeeVal = 0;
          let total = parseFloat(split.total_amount || 0);

          if (isItemWise) {
            if (tax > 0) {
              serviceTaxVal = tax;
            } else {
              serviceTaxVal = (subTotal * sTaxPct) / 100;
            }
            total = subTotal + serviceTaxVal;
          } else {
            // For equals / percentage / amount splits:
            if (sTaxPct > 0) {
              serviceTaxVal = total * (sTaxPct / (100 + sTaxPct));
              subTotal = total - serviceTaxVal;
            } else {
              subTotal = total;
              serviceTaxVal = 0;
            }
            platformFeeVal = 0;
          }

          return (
            <Card
              key={split.id}
              sx={{
                bgcolor: '#fff',
                color: '#000',
                borderRadius: '16px',
                border: isPaid ? '2px solid #2e7d32' : '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                position: 'relative'
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                {/* Card Top */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ color: '#000', fontWeight: 700 }}>
                    Bill No: {split.bill_number}
                  </Typography>
                  {isPaid ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#2e7d32', gap: 0.5 }}>
                      <CheckCircleIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Paid</Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 700, bgcolor: 'rgba(239, 68, 68, 0.1)', px: 1, py: 0.5, borderRadius: '4px' }}>
                      Unpaid
                    </Typography>
                  )}
                </Box>

                {split.guest_name && (
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                    Guest: <span style={{ color: '#000', fontWeight: 600 }}>{split.guest_name}</span>
                  </Typography>
                )}

                {/* Items List (if item_wise) */}
                {split.splitItems && split.splitItems.length > 0 && (
                  <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: '8px', border: '1px solid #e2e8f0', mb: 1.5 }}>
                    {split.splitItems.map((item, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                        <Typography variant="body2" sx={{ color: '#334155' }}>
                          {item.quantity}x {item.menu_item_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000', fontWeight: 600 }}>
                          {symbol}{parseFloat(item.total_price).toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Pricing Summary */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Sub Total:</Typography>
                  <Typography variant="body2" sx={{ color: '#000' }}>{symbol}{subTotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>Service Tax ({sTaxPct}%):</Typography>
                  <Typography variant="body2" sx={{ color: '#000' }}>{symbol}{serviceTaxVal.toFixed(2)}</Typography>
                </Box>
                {pFeePct > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>Platform Fee ({pFeePct}%):</Typography>
                    <Typography variant="body2" sx={{ color: '#000' }}>{symbol}{platformFeeVal.toFixed(2)}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: '#000', fontWeight: 700 }}>Total Bill Amount:</Typography>
                  <Typography variant="h6" sx={{ color: '#000', fontWeight: 700 }}>{symbol}{total.toFixed(2)}</Typography>
                </Box>

                {/* Card Actions */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {/* <CustomButton
                    onClick={() => onPrintSplitBill(split)}
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    sx={{
                      color: '#000',
                      borderColor: '#000',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      height: '40px',
                      flex: 1
                    }}
                  >
                    Print Bill
                  </CustomButton> */}

                  {!isPaid && !isOrderFinalized && (
                    <CustomButton
                      onClick={() => handleOpenPayModal(split)}
                      disabled={isPaying}
                      startIcon={<PaymentIcon />}
                      sx={{
                        backgroundColor: '#000',
                        color: '#fff',
                        borderRadius: '8px',
                        height: '40px',
                        flex: 1,
                        '&:hover': { backgroundColor: '#1f2937' }
                      }}
                    >
                      Pay Bill
                    </CustomButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Pay Split Dialog */}
      <Dialog
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: "#fff",
            width: "380px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "18px",
            textAlign: "center",
            fontWeight: 400,
            color: "#919191",
            pb: 1,
          }}
        >
          Collecting Payment for order
          <CustomTypography
            component="div"
            label={`#${selectedSplitToPay?.bill_number || orderDetails?.order_id}`}
            sx={{ fontWeight: 600, fontSize: "18px", mt: 0.5, color: "#000" }}
          />
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", pt: 0 }}>
          <Box sx={{ my: 2 }}>
            <CustomTypography
              variant={"body1"}
              label={"Total Payable Amount"}
              sx={{ fontSize: "16px", fontWeight: 400, color: "#919191", mb: 0.5 }}
            />

            <CustomTypography
              variant={"h6"}
              label={(() => {
                if (!selectedSplitToPay) return `${symbol}0.00`;
                const isItemWise = selectedSplitToPay.split_type === 'item_wise';
                if (!isItemWise) {
                  return `${symbol}${parseFloat(selectedSplitToPay.total_amount || 0).toFixed(2)}`;
                }

                const sTotal = parseFloat(selectedSplitToPay?.sub_total || 0);
                const sTax = parseFloat(selectedSplitToPay?.tax_amount || 0);

                const sTaxPct = parseFloat(vendorData?.service_tax || 0);
                let serviceTaxVal = 0;

                if (sTax > 0) {
                  serviceTaxVal = sTax;
                } else {
                  serviceTaxVal = (sTotal * sTaxPct) / 100;
                }

                const finalVal = sTotal + serviceTaxVal;
                return `${symbol}${finalVal.toFixed(2)}`;
              })()}
              sx={{ fontSize: "30px", fontWeight: 600, color: "#2E7D32", mb: 0 }}
            />
          </Box>

          <Divider />

          {/* Payment Mode */}
          <Box sx={{ mt: 3 }}>
            <CustomTypography
              variant={"body2"}
              sx={{ fontSize: "14px", fontWeight: 500, mb: 1, color: "#919191" }}
              label={"Select Payment Mode"}
            />
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <CustomSelect
                name="payment_method"
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                options={paymentMethodOptions}
                formWidth={"60%"}
                textAlign={"left"}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              mt: 4,
              mb: 1,
            }}
          >
            <CustomButton
              variant="outlined"
              onClick={() => setPayModalOpen(false)}
              sx={{
                px: 3,
                fontSize: "14px",
                borderColor: "#000",
                color: "#000",
                backgroundColor: "#fff",
                borderRadius: '10px',
                height: '45px'
              }}
            >
              Cancel
            </CustomButton>

            <CustomButton
              sx={{ width: '180px', borderRadius: '10px', height: '45px' }}
              onClick={handleConfirmPay}
            >
              Collect Payment
            </CustomButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SplitBillsView;
